/**
 * Cliente fino do tool `transcribe` do wuweiAgent.
 *
 * O Messenger só decodifica o áudio (qualquer formato → Float32 mono 16kHz)
 * e recorta silêncio nas extremidades (Whisper alucina em segmentos vazios).
 * A inferência roda no worker pool do Agent — main thread fica livre.
 *
 * O `langHint` ainda é honrado (mapeado pro nome em inglês esperado pelo
 * tool). `contextHint` (initial prompt) e parâmetros de decode (beam search,
 * temperature) ficaram fora do tool atual; reintroduzir requer mexer no
 * wuweiAgent. Isso é trade-off explícito da migração.
 */

import { getPool, newInvocationId } from "./agent-pool.js";
import { recursoAtivo } from "./inferencia.js";

type ProgressInfo =
  | { status: "initiate" | "ready" | "done"; name?: string; file?: string }
  | { status: "downloading"; name: string; file: string; progress: number; loaded: number; total: number }
  | { status: "progress"; name: string; file: string; progress: number };

export type WhisperProgress = (info: ProgressInfo) => void;

const progressListeners: Set<WhisperProgress> = new Set();

export function onWhisperProgress(cb: WhisperProgress): () => void {
  progressListeners.add(cb);
  return () => progressListeners.delete(cb);
}

/** Decodifica um blob de áudio em Float32Array mono a 16kHz. */
async function blobTo16kMono(blob: Blob): Promise<Float32Array> {
  const ab = await blob.arrayBuffer();
  const Ctx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const probe = new Ctx();
  let decoded: AudioBuffer;
  try {
    decoded = await probe.decodeAudioData(ab.slice(0));
  } finally {
    probe.close().catch(() => {});
  }
  const targetRate = 16000;
  const numFrames = Math.ceil(decoded.duration * targetRate);
  const off = new OfflineAudioContext(1, numFrames, targetRate);
  const src = off.createBufferSource();
  src.buffer = decoded;
  if (decoded.numberOfChannels > 1) {
    const merger = off.createChannelMerger(1);
    src.connect(merger);
    merger.connect(off.destination);
  } else {
    src.connect(off.destination);
  }
  src.start();
  const rendered = await off.startRendering();
  return rendered.getChannelData(0).slice();
}

/** Recorta silêncio nas extremidades. RMS por frame de 30ms. */
function trimSilence(audio: Float32Array, sampleRate = 16000): Float32Array {
  const frameMs = 30;
  const frameSize = Math.round((sampleRate * frameMs) / 1000);
  const numFrames = Math.floor(audio.length / frameSize);
  if (numFrames < 4) return audio;

  const energies = new Float32Array(numFrames);
  for (let i = 0; i < numFrames; i++) {
    let sum = 0;
    const start = i * frameSize;
    for (let j = 0; j < frameSize; j++) {
      const s = audio[start + j];
      sum += s * s;
    }
    energies[i] = Math.sqrt(sum / frameSize);
  }

  const sorted = Array.from(energies).sort((a, b) => a - b);
  const noiseFloor = sorted[Math.floor(sorted.length * 0.3)] || 0;
  const threshold = Math.max(noiseFloor * 2.5, 0.005);

  let first = 0;
  let last = numFrames - 1;
  while (first < numFrames && energies[first] < threshold) first++;
  while (last > first && energies[last] < threshold) last--;
  if (first >= last) return audio;

  const margin = 5; // 150ms
  first = Math.max(0, first - margin);
  last = Math.min(numFrames - 1, last + margin);

  return audio.slice(first * frameSize, (last + 1) * frameSize);
}

interface TranscribeInput {
  audio: Float32Array;
  language?: string | null;
  task?: "transcribe" | "translate";
  model?: string;
}
interface TranscribeOutput {
  text: string;
  seconds: number;
  model: string;
  ms: number;
}

export interface TranscribeOpts {
  langHint?: string;
  /** Reservado pra quando o tool aceitar initial prompt (não suportado hoje). */
  contextHint?: string;
}

export async function transcribeBlob(
  blob: Blob,
  opts: TranscribeOpts = {},
): Promise<{ text: string; lang: string }> {
  if (!blob.size) return { text: "", lang: "" };
  // Segunda linha de defesa: quem chega aqui sem checar whisperSupported()
  // sai sem transcript em vez de carregar o modelo.
  if (!recursoAtivo("transcricao")) return { text: "", lang: "" };
  const decoded = await blobTo16kMono(blob);
  const audio = trimSilence(decoded);

  const language = mapLang(opts.langHint) ?? "auto";
  const pool = getPool();
  const invId = newInvocationId("whisper");
  const result = await pool.runTool<TranscribeInput, TranscribeOutput>(
    invId,
    "transcribe",
    {
      audio,
      language,
      task: "transcribe",
      model: "Xenova/whisper-base",
    },
  );
  return { text: (result.text || "").trim(), lang: opts.langHint || "" };
}

function mapLang(hint?: string): string | undefined {
  if (!hint) return undefined;
  const head = hint.toLowerCase().split("-")[0];
  switch (head) {
    case "pt":
      return "portuguese";
    case "en":
      return "english";
    case "es":
      return "spanish";
    case "fr":
      return "french";
    case "it":
      return "italian";
    case "de":
      return "german";
    default:
      return undefined;
  }
}

export function whisperSupported(): boolean {
  if (!recursoAtivo("transcricao")) return false;
  return (
    typeof WebAssembly !== "undefined" &&
    typeof AudioContext !== "undefined" &&
    typeof OfflineAudioContext !== "undefined"
  );
}
